import { useContext } from 'react';
import './App.css';
import AuthGuard from './components/shared/AuthGuard';
import DashboardPageV2 from './pages/DashboardV2';
import DashboardPage from './pages/Dashboard';
import DistributorDashboardPage from './pages/orderly-for-distributors/DistributorDashboard';
import LoginPage from './pages/Login';
import TransactionsPage from './pages/Transactions';
import CashManagementPage from './pages/CashManagement';
import CashReconciliationPage from './pages/CashReconciliation';
import MobileTillDashboard from './pages/CashReconciliation/mobile/MobileTillDashboard';
import MobileCountScreen from './pages/CashReconciliation/mobile/MobileCountScreen';
import { UserContextProvider, UserContext } from './components/shared/useUser';
import { ConfigurationContextProvider } from './components/shared/useConfigurations';
import { InstanceMarketProvider } from './contexts/InstanceMarketContext';
import { Navigate, RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import Layout from './components/shared/layout/Layout';
import Reporting from './pages/Reporting';
import ReportingV2 from './pages/ReportingV2';
import { allReportPermissions } from './pages/Reporting/components/shared/ReportTypesData';
import ConfigurationPreviewRoute from './components/shared/ConfigurationPreviewRoute';
import { ConfigurationName } from './enums/ConfigurationName';
import StockCountSchedule from './pages/StockCountSchedule';
import StockCountDetailsPage from './pages/StockCount';
import StockTransfersPage from './pages/StockTransfers';
import PurchaseOrdersPage from './pages/PurchaseOrders';
import CreateStockCount from './pages/CreateStockCount';
import StockCounts from './pages/StockCountDetails';
import StockCountReviewPage from './pages/StockCountReview';
import UomConversions from './pages/UomCoversions';
import RecipePage from './pages/Recipe';
import RecipeV2Page from './pages/RecipeV2';
import RecipeV2DetailPage from './pages/RecipeV2/RecipeV2Detail';
import RecipeV2CreatePage from './pages/RecipeV2/RecipeV2Create';
import RangesPage from './pages/Range/Ranges';
import RangeForm from './pages/Range/Ranges/RangeForm';
import MenusPage from './pages/Range/Menus';
import MenuForm from './pages/Range/Menus/MenuForm';
import DataQualityDashboard from './pages/DataQuality';
import OrdersHistoryPage from './pages/Orders/OrdersHistory';
import QuickOrderPage from './pages/Orders/QuickOrder';
import OrderDetailsPage from './pages/Orders/OrderDetails';
import ProductsPage from './pages/Products';
import OrderrableItemsPage from './pages/OrderItems';
import StorefrontPage from './pages/Storefront';
import StockOnHandPage from './pages/StockOnHand';
import ProductDetailsPage from './pages/ProductDetails';
import Invoices from './pages/Invoices';
import InvoiceDetails from './pages/InvoiceDetails';
import StockTransferDetailPage from './pages/StockTransfersDetails';
import WasteChecksheetPage from './pages/WasteChecksheet';
import WasteCheckSheetDetailsPage from './pages/WasteChecksheetDetail';
import ManageNotifications from './pages/ManageNotifications';
import ScheduledReports from './pages/ScheduledReports';
import CreateScheduledReports from './pages/CreateScheduledReports';
import ManageTransactions from './pages/TransactionsManagement';
import PurchaseOrdersDetails from './pages/PurchaseOrdersDetails';
import StockCountZonesPage from './pages/StockCountZones';
import OrderFormPage from './pages/Orders/OrderForm';
import BulkOrderManagementPage from './pages/Orders/BulkOrderManagement';
import DemandModifiersPage from './pages/Orders/DemandModifiers';
import Support from './pages/Support';
import SettingsPage from './pages/Settings';
import StockCountScheduleCreate from './pages/StockCountScheduleCreate';
import ManageConfigurations from './pages/ManageConfigurations';
import ErrorPage from './pages/ErrorPage';
import ManageCategories from './pages/ManageCategories';
import CreditRequest from './pages/CreditDetails';
import SourceTransactionDetails from './pages/SourceTransactions';
import ManagePodConfigurations from './pages/ManagePodConfigurations';
import ManageBranding from './pages/ManageBranding';
//Orderly for distributors pages
import ManageRoles from './pages/orderly-for-distributors/ManageRoles';
import ManageUsers from './pages/orderly-for-distributors/ManageUsers';
import UserManagementUsersPage from './pages/UserManagement/Users';
import UserDetailPage from './pages/UserManagement/Users/UserDetail';
import UserManagementRolesPage from './pages/UserManagement/Roles';
import RoleDetailPage from './pages/UserManagement/Roles/RoleDetail';
import ManageCatalogues from './pages/orderly-for-distributors/Catalogues';
import OrdersList from './pages/orderly-for-distributors/OrdersList';
import DeliveriesList from './pages/orderly-for-distributors/Deliveries/DeliveriesList';
import ProductsListOfd from './pages/orderly-for-distributors/OrderableItems';
import OnboardingWizard from './pages/orderly-for-distributors/Onboarding';
import Finance from './pages/orderly-for-distributors/Finance';
import DeliveriesPage from './pages/orderly-for-distributors/PurchaseOrders';
import DeliveryDetails from './pages/orderly-for-distributors/PurchaseOrdersDetails';
import DistributorCreditRequest from './pages/orderly-for-distributors/CreditDetails';
import ManageRetailers from './pages/orderly-for-distributors/ManageRetailers';
import CatalogueDetails from './pages/orderly-for-distributors/CatalogueDetails';
import { GoogleMapsProvider } from './utils/maps/GoogleMapsProvider';
import ProductDetails from './pages/orderly-for-distributors/ProductDetails';
import OrderDetailsPageOfd from './pages/orderly-for-distributors/OrderDetailsOfd';
import { useLanguageSwitcher } from './hooks/useLanguageSwitcher';
import I18n from './i18n';
import ManageExchangeRates from './pages/orderly-for-distributors/ExchangeRate';
import ManageItemTags from './pages/orderly-for-distributors/ItemTags';
import ItemTagEditorPage from './pages/orderly-for-distributors/ItemTags/components/ItemTagEditor';
import DeliveryPricingPage from './pages/orderly-for-distributors/DeliveryPricing';
import ManagePromotions from './pages/orderly-for-distributors/Promotions';
import PromotionEditor from './pages/orderly-for-distributors/Promotions/components/PromotionEditor';
import { getApiConfig } from './app/services/environment';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import UserProfilePage from './pages/UserProfile';
import NotificationPreferencesPage from './pages/NotificationPreferences';
import InstancesPage from './pages/Instances';
import InstanceFormPage from './pages/Instances/InstanceForm';
import MarketsPage from './pages/Markets';
import MarketFormPage from './pages/Markets/MarketForm';
import OrderTypesPage from './pages/OrderTypes';
import OrderTypeFormPage from './pages/OrderTypes/OrderTypeForm';
import SuppliersPage from './pages/Suppliers';
import SupplierFormPage from './pages/Suppliers/SupplierForm';
import ProcurementSupplierPage from './pages/Procurement/Suppliers';
import ProcurementSupplierFormPage from './pages/Procurement/Suppliers/SupplierForm';
import ProcurementOrderChannelsPage from './pages/Procurement/OrderChannels';
import ProcurementOrderChannelFormPage from './pages/Procurement/OrderChannels/OrderChannelForm';
import CataloguesPage from './pages/Catalogues';
import CatalogueFormPage from './pages/Catalogues/CatalogueForm';
import ProcurementCataloguesPage from './pages/Procurement/Catalogues';
import ProcurementCatalogueFormPage from './pages/Procurement/Catalogues/CatalogueForm';
import StoresPage from './pages/Stores';
import StoreFormPage from './pages/Stores/StoreForm';
import UomTypesPage from './pages/UomTypes';
import UomTypeFormPage from './pages/UomTypes/UomTypeForm';
import CategoriesPage from './pages/Categories';
import CategoryFormPage from './pages/Categories/CategoryForm';
import ProcurementCategoriesPage from './pages/Procurement/Categories';
import ProcurementCategoryFormPage from './pages/Procurement/Categories/CategoryForm';
import ProcurementDeliverySchedulesPage from './pages/Procurement/DeliverySchedules';
import ProductManagementPage from './pages/Items';
import ProductForm from './pages/Items/ProductForm';
import ProcurementProductManagementPage from './pages/Procurement/Products';
import ProcurementProductForm from './pages/Procurement/Products/ProductForm';
import ProcurementPromotionsPage from './pages/Procurement/Promotions';
import ProcurementPromotionForm from './pages/Procurement/Promotions/PromotionForm';
import StockCountZones from './pages/StockCountZonesWithItems';
// Compliance pages
import ComplianceDashboard from './pages/Compliance/Dashboard';
import AuditTypesPage from './pages/Compliance/AuditTypes';
import AuditTemplatesPage from './pages/Compliance/AuditTemplates';
import AuditHistoryPage from './pages/Compliance/AuditHistory';
import TrainingCategoriesPage from './pages/Compliance/TrainingCategories';
import TrainingModulesPage from './pages/Compliance/TrainingModules';
import MyTrainingPage from './pages/Compliance/MyTraining';
import ActionItemsPage from './pages/Compliance/ActionItems';
import ActionItemDetailPage from './pages/Compliance/ActionItemDetail';
import AuditExecutionPage from './pages/Compliance/AuditExecution';
import AuditReviewPage from './pages/Compliance/AuditReview';
import AuditTemplateBuilderPage from './pages/Compliance/AuditTemplateBuilder';
import TrainingModuleBuilderPage from './pages/Compliance/TrainingModuleBuilder';
import TrainingCoursesPacksPage from './pages/Compliance/TrainingCoursesPacks';
import TrainingBundleBuilderPage from './pages/Compliance/TrainingBundleBuilder';
import TrainingAssignmentsPage from './pages/Compliance/TrainingAssignments';
import AuditSchedulesPage from './pages/Compliance/AuditSchedules';
import ComplianceReportsPage from './pages/Compliance/ComplianceReports';
import TakeTrainingPage from './pages/Compliance/TakeTraining';
import AutoshipmentTemplates from './pages/ManageAutoshipments/AutoshipmentTemplate/components/AutoshipmentTemplates';
import AutoshipmentSchedules from './pages/ManageAutoshipments/AutoshipmentSchedule/components/AutoshipmentSchedules';
import CreateOrEditAutoshipmentSchedule from './pages/ManageAutoshipments/AutoshipmentSchedule/components/CreateorEditAutoshipmentSchedule';
import CreateOrEditAutoshipmentTemplate from './pages/ManageAutoshipments/AutoshipmentTemplate/components/CreateOrEditAutoshipmentTemplate';
import ManageTaskCategories from './pages/TaskCategoriesManagement';
import TaskTemplatesPage from './pages/TaskTemplates';
import TaskTemplateDetails from './pages/TaskTemplates/TaskTemplateDetails';
import TaskSchedulesPage from './pages/TaskSchedules';
import TaskScheduleDetails from './pages/TaskSchedules/TaskScheduleDetails';
import OperationalDaysPage from './pages/OperationalDays';
import OperationalDayDetails from './pages/OperationalDays/OperationalDayDetails';
import OperationsClockDashboard from './pages/OperationalDays/OperationsClockDashboard';
import TasksPage from './pages/Tasks';
import TaskDetails from './pages/Tasks/TaskDetails';
import CreateAdhocTask from './pages/Tasks/CreateAdhocTask';
import TaskReportingDashboard from './pages/TaskReportingDashboard';
// Document Management
import DocumentsPage from './pages/Documents';
import DocumentUpload from './pages/Documents/DocumentUpload';
import DocumentView from './pages/Documents/DocumentView';
import FolderEdit from './pages/Documents/FolderEdit';
import FolderPermissions from './pages/Documents/FolderPermissions';
// Automated Workflows
import AutomatedWorkflowsPage from './pages/AutomatedWorkflows';
import AutomatedWorkflowDetails from './pages/AutomatedWorkflows/AutomatedWorkflowDetails';
// News Management
import NewsManagementPage from './pages/NewsManagement';
import NewsItemDetails from './pages/NewsManagement/NewsItemDetails';
import DeliveryDetailsOfd from './pages/orderly-for-distributors/Deliveries/DeliveryDetails';
import CreditRequestsOfd from './pages/orderly-for-distributors/Deliveries/CreditRequests';
import ManageDeliverySchedule from './pages/ManageDeliverySchedule';
import BannersPage from './pages/ContentManagement/Banners';
import BannerFormPage from './pages/ContentManagement/Banners/BannerFormPage';
import POSMappingManagerPage from './pages/PosMappingsManager';
// Admin pages
import TenantsPage from './pages/Admin/Tenants';
import TenantFormPage from './pages/Admin/Tenants/TenantForm';
import ModulesPage from './pages/Admin/Modules';
import ModuleFormPage from './pages/Admin/Modules/ModuleForm';
import SystemsAdminPage from './pages/Admin/Systems';
import SystemFormPage from './pages/Admin/Systems/SystemForm';
import PermissionsPage from './pages/Admin/Permissions';
import { System } from './enums/System';
import { isDemoModeEnabled } from './utils/featureAccess';

const AppContent = ({ apiKey }: { apiKey: string | undefined }) => {
  const { isLoaded, isSignedIn } = useAuth();
  const { selectedStore } = useContext(UserContext);

  if (!isLoaded) {
    return null; // or a loading spinner
  }

  if (isSignedIn && selectedStore) {
    return (
      <InstanceMarketProvider>
        <GoogleMapsProvider apiKey={apiKey ?? ''}>
          <RoutingWrapper />
        </GoogleMapsProvider>
      </InstanceMarketProvider>
    );
  }

  return <LoginPage />;
};

const RoutingWrapper = () => {
  const { selectedSystem, user, hasPermissionTo, selectedFranchise } = useContext(UserContext);
  const isDistributorSystem = selectedSystem?.id === System.OrderlyForDistributors;
  const isDemoMode = isDemoModeEnabled();
  const isStoreOnlyUser = isDistributorSystem && !selectedFranchise;
  const defaultHomePage = isStoreOnlyUser && hasPermissionTo(['StorefrontView'])
    ? <StorefrontPage />
    : isDistributorSystem ? <DistributorDashboardPage /> :
      (isDemoMode ? <DashboardPageV2 /> : <DashboardPage />);

  const routes: RouteObject[] = [
    {
      path: '/',
      element: <AuthGuard permissions={['User']}><Layout>{defaultHomePage}</Layout></AuthGuard>,
    },
    {
      path: '/dashboardV2',
      element: <AuthGuard permissions={[((user?.emailAddress?.toLowerCase()?.indexOf('@orderly.io') || -1) > 0 ? 'User' : 'Admin')]}><Layout>{isDistributorSystem ? <DistributorDashboardPage /> : <DashboardPageV2 />}</Layout></AuthGuard>,
    },
    {
      path: '/reports',
      element: (
        <AuthGuard permissions={[...allReportPermissions]}>
          <Layout>
            <ConfigurationPreviewRoute
              configurationName={ConfigurationName.ReportsV2Enabled}
              legacyPath="/reports"
              previewPath="/reports/v2"
              version="legacy">
              <Reporting />
            </ConfigurationPreviewRoute>
          </Layout>
        </AuthGuard>
      ),
    },
    {
      path: '/reports/v2',
      element: (
        <AuthGuard permissions={[...allReportPermissions]}>
          <Layout>
            <ConfigurationPreviewRoute
              configurationName={ConfigurationName.ReportsV2Enabled}
              legacyPath="/reports"
              previewPath="/reports/v2"
              version="preview">
              <ReportingV2 />
            </ConfigurationPreviewRoute>
          </Layout>
        </AuthGuard>
      ),
    },
    {
      path: '/store/stock-count',
      element: <AuthGuard permissions={['StockCountAdmin', 'StockCountRead']}><Layout><StockCounts /></Layout></AuthGuard>,
    },
    {
      path: '/store/create-stock-count',
      element: <AuthGuard permissions={['User']}><Layout><CreateStockCount /></Layout></AuthGuard>,
    },
    {
      path: '/store/review-stock-count/:id',
      element: <AuthGuard permissions={['User']}><Layout><StockCountReviewPage /></Layout></AuthGuard>,
    },
    {
      path: '/stock-count-schedule/:tab?',
      element: <AuthGuard permissions={['StockCountAdmin', 'StockCountUomsRead', 'StockLocationRead', 'StockCountScheduleRead']}><Layout><StockCountSchedule /></Layout></AuthGuard>,
    },
    {
      path: '/invoices',
      element: <AuthGuard permissions={['InvoiceReconciliationWrite', 'InvoiceReconciliationAdmin', 'InvoiceReconciliationRead']}><Layout><Invoices /></Layout></AuthGuard>,
    },
    {
      path: '/invoice/:id',
      element: <AuthGuard permissions={['InvoiceReconciliationWrite', 'InvoiceReconciliationAdmin', 'InvoiceReconciliationRead']}><Layout><InvoiceDetails /></Layout></AuthGuard>,
    },
    {
      path: '/store/perform-stock-count/:id',
      element: <AuthGuard permissions={['User']}><Layout><StockCountDetailsPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/stock-transfers',
      element: <AuthGuard permissions={['TransferRead']}><Layout><StockTransfersPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/stock-transfer/:id',
      element: <AuthGuard permissions={['TransferRead']}><Layout><StockTransferDetailPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/goods-receipts',
      element: <AuthGuard permissions={['PurchaseOrderRead']}><Layout><PurchaseOrdersPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/orders-history',
      element: <AuthGuard permissions={['User']}><Layout><OrdersHistoryPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/quick-order/:id',
      element: <AuthGuard permissions={['User']}><Layout><QuickOrderPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/quick-order',
      element: <AuthGuard permissions={['User']}><Layout><QuickOrderPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/order/:id',
      element: <AuthGuard permissions={['User']}><Layout><OrderDetailsPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/products',
      element: <AuthGuard permissions={['ItemRead']}><Layout><ProductsPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/storefront',
      element: <AuthGuard permissions={['StorefrontView']}><Layout><StorefrontPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/orderable-items',
      element: <AuthGuard permissions={['OrderableItemsRead']}><Layout><OrderrableItemsPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/order-planning',
      element: <AuthGuard permissions={['User']}><Layout><OrderFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/bulk-order-management',
      element: <AuthGuard permissions={['BulkOrdersRead']}><Layout><BulkOrderManagementPage /></Layout></AuthGuard>,
    },
    {
      path: '/demand-modifiers',
      element: <AuthGuard permissions={['BulkOrdersRead']}><Layout><DemandModifiersPage /></Layout></AuthGuard>,
    },
    {
      path: '/create-edit-schedule-stock-count/:id',
      element: <AuthGuard permissions={['StockCountScheduleRead', 'StockCountAdmin']}><Layout><StockCountScheduleCreate /></Layout></AuthGuard>,
    },
    {
      path: '/create-edit-schedule-stock-count',
      element: <AuthGuard permissions={['StockCountScheduleRead', 'StockCountAdmin']}><Layout><StockCountScheduleCreate /></Layout></AuthGuard>,
    },
    {
      path: '/transactions/:id',
      element: <AuthGuard permissions={['TransactionRead']}><Layout><TransactionsPage /></Layout></AuthGuard>,
    },
    {
      path: '/transactions',
      element: <AuthGuard permissions={['TransactionRead']}><Layout><TransactionsPage /></Layout></AuthGuard>,
    },
    {
      path: '/cash-management',
      element: <AuthGuard permissions={['CashManagementView', 'CashManagementCreate', 'CashManagementEdit', 'TransactionRead']}
        status="demo"><Layout><CashManagementPage /></Layout></AuthGuard>,
    },
    {
      path: '/cash-reconciliation',
      element: <AuthGuard permissions={['CashReconciliationRead']}
        status="demo"><Layout><CashReconciliationPage /></Layout></AuthGuard>,
    },
    {
      path: '/cash-reconciliation/mobile',
      element: <AuthGuard permissions={['CashReconciliationRead']}
        status="demo"><Layout><MobileTillDashboard /></Layout></AuthGuard>,
    },
    {
      path: '/cash-reconciliation/mobile/count/:tillNumber',
      element: <AuthGuard permissions={['CashReconciliationWrite']}
        status="demo"><Layout><MobileCountScreen /></Layout></AuthGuard>,
    },
    {
      path: '/manage-item-mapping',
      element: <AuthGuard permissions={['POSMapping']}><Layout><POSMappingManagerPage /></Layout></AuthGuard>,
    },
    {
      path: '/uom-conversions/:itemNumber',
      element: <AuthGuard permissions={['ConversionRead']}><Layout><UomConversions /></Layout></AuthGuard>,
    },
    {
      path: '/uom-conversions',
      element: <AuthGuard permissions={['ConversionRead']}><Layout><UomConversions /></Layout></AuthGuard>,
    },
    {
      path: '/store/stock-count-zones',
      element: <AuthGuard permissions={['User']}><Layout><StockCountZonesPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/stock',
      element: <AuthGuard permissions={['StockCountRead']}><Layout><StockOnHandPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/item/:id',
      element: <AuthGuard permissions={['User']}><Layout><ProductDetailsPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/waste-logs',
      element: <AuthGuard permissions={['DailyWasteCheckSheetRead']}><Layout><WasteChecksheetPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/daily-waste-checksheet/:id',
      element: <AuthGuard permissions={['DailyWasteCheckSheetRead']}><Layout><WasteCheckSheetDetailsPage /></Layout></AuthGuard>,
    },
    {
      path: '/store/recipes',
      element: <AuthGuard permissions={['RecipeRead']}><Layout><RecipePage /></Layout></AuthGuard>,
    },
    {
      path: '/store/recipes-v2',
      element: <AuthGuard permissions={['RecipeV2Read']}
        status="demo"><Layout><RecipeV2Page /></Layout></AuthGuard>,
    },
    {
      path: '/store/recipes-v2/new',
      element: <AuthGuard permissions={['RecipeV2Write']}
        status="demo"><Layout><RecipeV2CreatePage /></Layout></AuthGuard>,
    },
    {
      path: '/store/recipes-v2/:id',
      element: <AuthGuard permissions={['RecipeV2Read']}
        status="demo"><Layout><RecipeV2DetailPage /></Layout></AuthGuard>,
    },
    {
      path: '/range/ranges',
      element: <AuthGuard permissions={['RangeRead']}><Layout><RangesPage /></Layout></AuthGuard>,
    },
    {
      path: '/range/ranges/new',
      element: <AuthGuard permissions={['RangeWrite']}><Layout><RangeForm /></Layout></AuthGuard>,
    },
    {
      path: '/range/ranges/:rangeId',
      element: <AuthGuard permissions={['RangeRead']}><Layout><RangeForm /></Layout></AuthGuard>,
    },
    {
      path: '/range/menus',
      element: <AuthGuard permissions={['MenuRead']}><Layout><MenusPage /></Layout></AuthGuard>,
    },
    {
      path: '/range/menus/new',
      element: <AuthGuard permissions={['MenuWrite']}><Layout><MenuForm /></Layout></AuthGuard>,
    },
    {
      path: '/range/menus/:menuId',
      element: <AuthGuard permissions={['MenuRead']}><Layout><MenuForm /></Layout></AuthGuard>,
    },
    {
      path: '/store/data-quality',
      element: <AuthGuard permissions={['RecipeV2RationaliseRead']}
        status="demo"><Layout><DataQualityDashboard /></Layout></AuthGuard>,
    },
    {
      path: '/manage-pod-configurations',
      element: <AuthGuard permissions={['AIOrderingAdmin', 'AIOrderingConfiguration']}
        requiredModule='Predictive Ordering Settings'><Layout><ManagePodConfigurations /></Layout></AuthGuard>,
    },
    {
      path: '/manage-categories',
      element: <AuthGuard permissions={['User']}><Layout><ManageCategories /></Layout></AuthGuard>,
    },
    {
      path: '/manage-transactions',
      element: <AuthGuard permissions={['TransactionAdmin']}><Layout><ManageTransactions /></Layout></AuthGuard>,
    },
    {
      path: '/manage-task-categories',
      element: <AuthGuard permissions={['TaskCategoriesView']}
        status="demo"><Layout><ManageTaskCategories /></Layout></AuthGuard>,
    },
    {
      path: '/store/transactions/source/:transactionSourceId/:transactionDate',
      element: <AuthGuard permissions={['SourceTransactionRead']}><Layout><SourceTransactionDetails /></Layout></AuthGuard>,
    },
    {
      path: '/create-goods-receipt/:id',
      element: <AuthGuard permissions={['User']}><Layout><PurchaseOrdersDetails /></Layout></AuthGuard>,
    },
    {
      path: '/store/credit-request/:id',
      element: <AuthGuard permissions={['User']}><Layout><CreditRequest /></Layout></AuthGuard>,
    },
    {
      path: '/scheduled-reports',
      element: <AuthGuard permissions={['User']}><Layout><ScheduledReports /></Layout></AuthGuard>,
    },
    {
      path: '/create-scheduled-reports/:id',
      element: <AuthGuard permissions={['User']}><Layout><CreateScheduledReports /></Layout></AuthGuard>,
    }, {
      path: '/role-management',
      element: <AuthGuard permissions={['RolesAdmin', 'RoleManagementView']}><Layout><ManageRoles /></Layout></AuthGuard>,
    },
    {
      path: '/user-management',
      element: <AuthGuard permissions={['UserAdmin', 'UserManagementView']}><Layout><ManageUsers /></Layout></AuthGuard>,
    },
    {
      path: '/user-management/users',
      element: <AuthGuard permissions={['UserAdmin', 'UserManagementView']}><Layout><UserManagementUsersPage /></Layout></AuthGuard>,
    },
    {
      path: '/user-management/users/create',
      element: <AuthGuard permissions={['UserAdmin', 'UserManagementCreate']}><Layout><UserDetailPage /></Layout></AuthGuard>,
    },
    {
      path: '/user-management/users/:id',
      element: <AuthGuard permissions={['UserAdmin', 'UserManagementView']}><Layout><UserDetailPage /></Layout></AuthGuard>,
    },
    //intentionally temporarily comment these routes until we have the role management pages ready for production
    // {
    //   path: '/role-management/roles',
    //   element: <AuthGuard permissions={['RolesAdmin', 'Admin']}><Layout><UserManagementRolesPage /></Layout></AuthGuard>,
    // },
    // {
    //   path: '/role-management/roles/create',
    //   element: <AuthGuard permissions={['RolesAdmin', 'Admin']}><Layout><RoleDetailPage /></Layout></AuthGuard>,
    // },
    // {
    //   path: '/role-management/roles/:id',
    //   element: <AuthGuard permissions={['RolesAdmin', 'Admin']}><Layout><RoleDetailPage /></Layout></AuthGuard>,
    // },
    {
      path: '/manage-exchange-rates',
      element: <AuthGuard permissions={['UserAdmin']}><Layout><ManageExchangeRates /></Layout></AuthGuard>,
    },
    {
      path: '/settings/delivery-pricing',
      element: (
        <AuthGuard permissions={['DeliveryPricingView']}>
          <Layout>
            {selectedSystem?.id === System.OrderlyForDistributors ? <DeliveryPricingPage /> : <ErrorPage variant='403' />}
          </Layout>
        </AuthGuard>
      ),
    },
    {
      path: '/item-tags',
      element: <AuthGuard permissions={['ItemTagView', 'ItemTagAdmin']}><Layout><ManageItemTags /></Layout></AuthGuard>,
    },
    {
      path: '/item-tags/create',
      element: <AuthGuard permissions={['ItemTagCreate', 'ItemTagAdmin']}><Layout><ItemTagEditorPage /></Layout></AuthGuard>,
    },
    {
      path: '/item-tags/edit/:itemTagId',
      element: <AuthGuard permissions={['ItemTagUpdate', 'ItemTagAdmin']}><Layout><ItemTagEditorPage /></Layout></AuthGuard>,
    },
    {
      path: '/item-tags/:itemTagId/items',
      element: <AuthGuard permissions={['ItemTagAssign', 'ItemTagAdmin']}><Layout><ItemTagEditorPage /></Layout></AuthGuard>,
    },
    {
      path: '/promotions',
      element: <AuthGuard permissions={['PromotionRead', 'PromotionAdmin']}><Layout><ManagePromotions /></Layout></AuthGuard>,
    },
    {
      path: '/promotions/create',
      element: <AuthGuard permissions={['PromotionWrite', 'PromotionAdmin']}><Layout><PromotionEditor /></Layout></AuthGuard>,
    },
    {
      path: '/promotions/edit/:promotionId',
      element: <AuthGuard permissions={['PromotionWrite', 'PromotionAdmin']}><Layout><PromotionEditor /></Layout></AuthGuard>,
    },
    {
      path: '/manage-configurations',
      element: <AuthGuard permissions={['ConfigurationAdmin']}><Layout><ManageConfigurations /></Layout></AuthGuard>,
    },
    {
      path: '/instances',
      element: <AuthGuard permissions={['InstanceView']}><Layout><InstancesPage /></Layout></AuthGuard>,
    },
    {
      path: '/instances/create',
      element: <AuthGuard permissions={['InstanceCreate']}><Layout><InstanceFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/instances/edit/:id',
      element: <AuthGuard permissions={['InstanceUpdate']}><Layout><InstanceFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/markets',
      element: <AuthGuard permissions={['MarketView']}><Layout><MarketsPage /></Layout></AuthGuard>,
    },
    {
      path: '/markets/create',
      element: <AuthGuard permissions={['MarketCreate']}><Layout><MarketFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/markets/edit/:id',
      element: <AuthGuard permissions={['MarketUpdate']}><Layout><MarketFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/order-types',
      element: <AuthGuard permissions={['OrderTypeView']}><Layout><OrderTypesPage /></Layout></AuthGuard>,
    },
    {
      path: '/order-types/create',
      element: <AuthGuard permissions={['OrderTypeCreate']}><Layout><OrderTypeFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/order-types/edit/:id',
      element: <AuthGuard permissions={['OrderTypeUpdate']}><Layout><OrderTypeFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/suppliers',
      element: <AuthGuard permissions={['SupplierView']}><Layout><SuppliersPage /></Layout></AuthGuard>,
    },
    {
      path: '/suppliers/create',
      element: <AuthGuard permissions={['SupplierCreate']}><Layout><SupplierFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/suppliers/edit/:id',
      element: <AuthGuard permissions={['SupplierUpdate']}><Layout><SupplierFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/suppliers',
      element: <AuthGuard permissions={['SupplierView']}><Layout><ProcurementSupplierPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/suppliers/create',
      element: <AuthGuard permissions={['SupplierCreate']}><Layout><ProcurementSupplierFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/suppliers/edit/:id',
      element: <AuthGuard permissions={['SupplierUpdate']}><Layout><ProcurementSupplierFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/order-channels',
      element: <AuthGuard permissions={['OrderTypeView']}><Layout><ProcurementOrderChannelsPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/order-channels/create',
      element: <AuthGuard permissions={['OrderTypeCreate']}><Layout><ProcurementOrderChannelFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/order-channels/edit/:id',
      element: <AuthGuard permissions={['OrderTypeUpdate']}><Layout><ProcurementOrderChannelFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/catalogues',
      element: <AuthGuard permissions={['CatalogueView']}><Layout><CataloguesPage /></Layout></AuthGuard>,
    },
    {
      path: '/catalogues/create',
      element: <AuthGuard permissions={['CatalogueCreate']}><Layout><CatalogueFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/catalogues/edit/:id',
      element: <AuthGuard permissions={['CatalogueUpdate']}><Layout><CatalogueFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/catalogues',
      element: <AuthGuard permissions={['CatalogueView']}><Layout><ProcurementCataloguesPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/catalogues/create',
      element: <AuthGuard permissions={['CatalogueCreate']}><Layout><ProcurementCatalogueFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/catalogues/edit/:id',
      element: <AuthGuard permissions={['CatalogueUpdate']}><Layout><ProcurementCatalogueFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/stores',
      element: <AuthGuard permissions={['StoreView']}><Layout><StoresPage /></Layout></AuthGuard>,
    },
    {
      path: '/stores/create',
      element: <AuthGuard permissions={['StoreCreate']}><Layout><StoreFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/stores/edit/:id',
      element: <AuthGuard permissions={['StoreUpdate']}><Layout><StoreFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/uom-types',
      element: <AuthGuard permissions={['UomTypeView']}><Layout><UomTypesPage /></Layout></AuthGuard>,
    },
    {
      path: '/uom-types/create',
      element: <AuthGuard permissions={['UomTypeCreate']}><Layout><UomTypeFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/uom-types/edit/:uomTypeId',
      element: <AuthGuard permissions={['UomTypeUpdate']}><Layout><UomTypeFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/categories',
      element: <AuthGuard permissions={['CategoryView']}><Layout><CategoriesPage /></Layout></AuthGuard>,
    },
    {
      path: '/categories/create',
      element: <AuthGuard permissions={['CategoryCreate']}><Layout><CategoryFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/categories/edit/:categoryId',
      element: <AuthGuard permissions={['CategoryUpdate']}><Layout><CategoryFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/categories',
      element: <AuthGuard permissions={['CategoryView']}><Layout><ProcurementCategoriesPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/categories/create',
      element: <AuthGuard permissions={['CategoryCreate']}><Layout><ProcurementCategoryFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/categories/edit/:categoryId',
      element: <AuthGuard permissions={['CategoryUpdate']}><Layout><ProcurementCategoryFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/delivery-schedules',
      element: <AuthGuard permissions={['ODSRead']}><Layout><ProcurementDeliverySchedulesPage /></Layout></AuthGuard>,
    },
    {
      path: '/products',
      element: <AuthGuard permissions={['ProductView']}><Layout><ProductManagementPage /></Layout></AuthGuard>,
    },
    {
      path: '/products/create',
      element: <AuthGuard permissions={['ProductCreate']}><Layout><ProductForm /></Layout></AuthGuard>,
    },
    {
      path: '/products/edit/:id',
      element: <AuthGuard permissions={['ProductUpdate']}><Layout><ProductForm /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/products',
      element: <AuthGuard permissions={['ProductView']}><Layout><ProcurementProductManagementPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/products/create',
      element: <AuthGuard permissions={['ProductCreate']}><Layout><ProcurementProductForm /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/products/edit/:id',
      element: <AuthGuard permissions={['ProductUpdate']}><Layout><ProcurementProductForm /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/promotions',
      element: <AuthGuard permissions={['PromotionRead', 'PromotionAdmin']}><Layout><ProcurementPromotionsPage /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/promotions/create',
      element: <AuthGuard permissions={['PromotionWrite', 'PromotionAdmin']}><Layout><ProcurementPromotionForm /></Layout></AuthGuard>,
    },
    {
      path: '/procurement/promotions/edit/:promotionId',
      element: <AuthGuard permissions={['PromotionWrite', 'PromotionAdmin']}><Layout><ProcurementPromotionForm /></Layout></AuthGuard>,
    },
    {
      path: 'manage-configurations/branding',
      element: <AuthGuard permissions={['FranchiseDynamicBrandingWrite']}><Layout><ManageBranding /></Layout></AuthGuard>,
    },
    {
      path: '/manage-notifications',
      element: <AuthGuard permissions={['NotificationWrite']}><Layout><ManageNotifications /></Layout></AuthGuard>,
    },
    {
      path: '/support',
      element: <AuthGuard permissions={['User']}><Layout><Support /></Layout></AuthGuard>,
    },
    {
      path: '/catalogue-list',
      element: <AuthGuard permissions={['CataloguesView']}><Layout><ManageCatalogues /></Layout></AuthGuard>,
    },
    {
      path: '/ofd/catalogueproducts/:id',
      element: <AuthGuard permissions={['CataloguesDetailView']}><Layout><CatalogueDetails /></Layout></AuthGuard>,
    },
    {
      path: '/ofd/products/:id',
      element: <AuthGuard permissions={['ProductDetailView']}><Layout><ProductDetails /></Layout></AuthGuard>,
    },
    {
      path: '/orders',
      element: <AuthGuard permissions={['OrdersView']}><Layout><OrdersList /></Layout></AuthGuard>,
    },
    {
      path: '/order/:id',
      element: <AuthGuard permissions={['OrdersView']}><Layout><OrderDetailsPageOfd /></Layout></AuthGuard>,
    },
    {
      path: '/deliveries',
      element: (
        <AuthGuard permissions={['DeliveriesView']}>
          <Layout>
            {selectedSystem?.id === 1 ? <DeliveriesPage /> : <DeliveriesList />}
          </Layout>
        </AuthGuard>
      ),
    },
    {
      path: '/finance',
      element: <AuthGuard permissions={['FinanceView']}><Layout><Finance /></Layout></AuthGuard>,
    },
    {
      path: '/deliveries/:id',
      element: (
        <AuthGuard permissions={['DeliveriesView']}>
          <Layout>
            {selectedSystem?.id === 1 ? <DeliveryDetails /> : <DeliveryDetailsOfd />}
          </Layout>
        </AuthGuard>
      ),
    },
    {
      path: '/credits/:id',
      element: <AuthGuard permissions={['FinanceView']}><Layout><DistributorCreditRequest /></Layout></AuthGuard>,
    },
    {
      path: '/deliveries/:id/credit',
      element: <AuthGuard permissions={['CreditRequestsView']}><Layout><CreditRequestsOfd /></Layout></AuthGuard>,
    },
    {
      path: '/products-list/:id',
      element: <AuthGuard permissions={['ProductDetailView']}><Layout><ProductsListOfd /></Layout></AuthGuard>,
    },
    {
      path: '/retailers',
      element: <AuthGuard permissions={['RetailersView']}><Layout><ManageRetailers /></Layout></AuthGuard>,
    },
    {
      path: '/retailers/:id',
      element: <AuthGuard permissions={['RetailersDetailsView']}><Layout><OnboardingWizard /></Layout></AuthGuard>,
    },
    {
      path: '/user-settings',
      element: <AuthGuard permissions={['User']}><Layout><UserProfilePage /></Layout></AuthGuard>,
    },
    {
      path: '/user-settings/notifications',
      element: <AuthGuard permissions={['User']}><Layout><NotificationPreferencesPage /></Layout></AuthGuard>,
    },
    {
      path: '/create-edit-count-zone',
      element: <AuthGuard permissions={['User']}><Layout><StockCountZones /></Layout></AuthGuard>,
    },
    {
      path: '/create-edit-count-zone/:id',
      element: <AuthGuard permissions={['User']}><Layout><StockCountZones /></Layout></AuthGuard>,
    },

    {
      path: '/operational-days',
      element: <AuthGuard permissions={['OperationalDaysView']}
        status="demo"><Layout><OperationalDaysPage /></Layout></AuthGuard>,
    },
    {
      path: '/operational-days/:id',
      element: <AuthGuard permissions={['OperationalDaysView']}
        status="demo"><Layout><OperationalDayDetails /></Layout></AuthGuard>,
    },
    {
      path: '/task-templates',
      element: <AuthGuard permissions={['TaskTemplatesViewUnrestricted']}
        status="demo"><Layout><TaskTemplatesPage /></Layout></AuthGuard>,
    },
    {
      path: '/task-templates/:id',
      element: <AuthGuard permissions={['TaskTemplatesViewUnrestricted']}
        status="demo"><Layout><TaskTemplateDetails /></Layout></AuthGuard>,
    },
    {
      path: '/task-schedules',
      element: <AuthGuard permissions={['TaskSchedulesView']}
        status="demo"><Layout><TaskSchedulesPage /></Layout></AuthGuard>,
    },
    {
      path: '/task-schedules/:id',
      element: <AuthGuard permissions={['TaskSchedulesView']}
        status="demo"><Layout><TaskScheduleDetails /></Layout></AuthGuard>,
    },
    {
      path: '/operational-day',
      element: <AuthGuard permissions={['OperationalDaysViewDashboard']}
        status="demo"><Layout><OperationsClockDashboard /></Layout></AuthGuard>,
    },
    {
      path: '/tasks',
      element: <AuthGuard permissions={['AdvancedTasksView']}
        status="demo"><Layout><TasksPage /></Layout></AuthGuard>,
    },
    {
      path: '/tasks/create',
      element: <AuthGuard permissions={['AdvancedTasksCreate']}
        status="demo"><Layout><CreateAdhocTask /></Layout></AuthGuard>,
    },
    {
      path: '/tasks/:id',
      element: <AuthGuard permissions={['AdvancedTasksView']}
        status="demo"><Layout><TaskDetails /></Layout></AuthGuard>,
    },
    {
      path: '/task-reporting',
      element: <AuthGuard permissions={['AdvancedTasksView']}
        status="demo"><Layout><TaskReportingDashboard /></Layout></AuthGuard>,
    },
    // Document Management Routes
    {
      path: '/documents',
      element: <AuthGuard permissions={['DocumentsView']}
        status="demo"><Layout><DocumentsPage /></Layout></AuthGuard>,
    },
    {
      path: '/documents/upload',
      element: <AuthGuard permissions={['DocumentsUpload']}
        status="demo"><Layout><DocumentUpload /></Layout></AuthGuard>,
    },
    {
      path: '/documents/view/:id',
      element: <AuthGuard permissions={['DocumentsView']}
        status="demo"><Layout><DocumentView /></Layout></AuthGuard>,
    },
    {
      path: '/documents/edit/:id',
      element: <AuthGuard permissions={['DocumentsEdit']}
        status="demo"><Layout><DocumentView /></Layout></AuthGuard>,
    },
    {
      path: '/documents/folder/:id/edit',
      element: <AuthGuard permissions={['DocumentsEdit']}
        status="demo"><Layout><FolderEdit /></Layout></AuthGuard>,
    },
    {
      path: '/documents/folder/:id/permissions',
      element: <AuthGuard permissions={['DocumentsManagePermissions']}
        status="demo"><Layout><FolderPermissions /></Layout></AuthGuard>,
    },
    // Automated Workflows Routes
    {
      path: '/automated-workflows',
      element: <AuthGuard permissions={['AutomatedWorkflowsView']}
        status="demo"><Layout><AutomatedWorkflowsPage /></Layout></AuthGuard>,
    },
    {
      path: '/automated-workflows/new',
      element: <AuthGuard permissions={['AutomatedWorkflowsCreate']}
        status="demo"><Layout><AutomatedWorkflowDetails /></Layout></AuthGuard>,
    },
    {
      path: '/automated-workflows/:id',
      element: <AuthGuard permissions={['AutomatedWorkflowsEdit']}
        status="demo"><Layout><AutomatedWorkflowDetails /></Layout></AuthGuard>,
    },
    // News Management Routes
    {
      path: '/news-management',
      element: <AuthGuard permissions={['NewsManagementView']}
        status="demo"><Layout><NewsManagementPage /></Layout></AuthGuard>,
    },
    {
      path: '/news-management/new',
      element: <AuthGuard permissions={['NewsManagementCreate']}
        status="demo"><Layout><NewsItemDetails /></Layout></AuthGuard>,
    },
    {
      path: '/news-management/:id',
      element: <AuthGuard permissions={['NewsManagementEdit']}
        status="demo"><Layout><NewsItemDetails /></Layout></AuthGuard>,
    },

    {
      path: '/manage-autoshipments/templates',
      element: <AuthGuard permissions={['AutoshipmentRead']}><Layout><AutoshipmentTemplates /></Layout></AuthGuard>,
    },
    {
      path: '/manage-autoshipments/template/:id',
      element: <AuthGuard permissions={['AutoshipmentRead']}><Layout><CreateOrEditAutoshipmentTemplate /></Layout></AuthGuard>,
    },
    {
      path: '/manage-autoshipments/template',
      element: <AuthGuard permissions={['AutoshipmentWrite']}><Layout><CreateOrEditAutoshipmentTemplate /></Layout></AuthGuard>,
    },
    {
      path: '/manage-autoshipments/schedules/:id',
      element: <AuthGuard permissions={['AutoshipmentRead']}><Layout><AutoshipmentSchedules /></Layout></AuthGuard>,
    },
    {
      path: '/manage-autoshipments/schedule/:id',
      element: <AuthGuard permissions={['AutoshipmentRead']}><Layout><CreateOrEditAutoshipmentSchedule /></Layout></AuthGuard>,
    },
    {
      path: '/manage-autoshipments/schedule/new/:id',
      element: <AuthGuard permissions={['AutoshipmentWrite']}><Layout><CreateOrEditAutoshipmentSchedule /></Layout></AuthGuard>,
    },
    {
      path: '/manage-delivery-schedules',
      element: <AuthGuard permissions={['ODSRead']}><Layout><ManageDeliverySchedule /></Layout></AuthGuard>,
    },
    {
      path: '/settings/content-management/banners',
      element: <AuthGuard permissions={['ContentManagementAdmin']}><Layout><BannersPage /></Layout></AuthGuard>,
    },
    {
      path: '/settings/content-management/banners/create',
      element: <AuthGuard permissions={['ContentManagementAdmin']}><Layout><BannerFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/settings/content-management/banners/edit/:id',
      element: <AuthGuard permissions={['ContentManagementAdmin']}><Layout><BannerFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/settings',
      element: <AuthGuard permissions={['User']}><Layout><SettingsPage /></Layout></AuthGuard>,
    },
    // Admin Management Routes
    {
      path: '/admin/tenants',
      element: <AuthGuard permissions={['TenantView']}><Layout><TenantsPage /></Layout></AuthGuard>,
    },
    {
      path: '/admin/tenants/create',
      element: <AuthGuard permissions={['TenantCreate']}><Layout><TenantFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/admin/tenants/edit/:id',
      element: <AuthGuard permissions={['TenantUpdate']}><Layout><TenantFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/admin/modules',
      element: <AuthGuard permissions={['ModuleView']}><Layout><ModulesPage /></Layout></AuthGuard>,
    },
    {
      path: '/admin/modules/create',
      element: <AuthGuard permissions={['ModuleCreate']}><Layout><ModuleFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/admin/modules/edit/:id',
      element: <AuthGuard permissions={['ModuleUpdate']}><Layout><ModuleFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/admin/systems',
      element: <AuthGuard permissions={['SystemView']}><Layout><SystemsAdminPage /></Layout></AuthGuard>,
    },
    {
      path: '/admin/systems/create',
      element: <AuthGuard permissions={['SystemCreate']}><Layout><SystemFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/admin/systems/edit/:id',
      element: <AuthGuard permissions={['SystemUpdate']}><Layout><SystemFormPage /></Layout></AuthGuard>,
    },
    {
      path: '/admin/permissions',
      element: <AuthGuard permissions={['PermissionView']}><Layout><PermissionsPage /></Layout></AuthGuard>,
    },
    // Compliance Module Routes
    {
      path: '/compliance/dashboard',
      element: <AuthGuard permissions={['ViewComplianceDashboard']}><Layout><ComplianceDashboard /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/audits/types',
      element: <AuthGuard permissions={['AuditTypesView']}><Layout><AuditTypesPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/audits/templates',
      element: <AuthGuard permissions={['AuditTemplatesView']}><Layout><AuditTemplatesPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/audits/templates/builder/:templateId?',
      element: <AuthGuard permissions={['AuditTemplatesView']}><Layout><AuditTemplateBuilderPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/audits/schedules',
      element: <AuthGuard permissions={['ScheduledAuditsCreate']}><Layout><AuditSchedulesPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/audits/history',
      element: <AuthGuard permissions={['AuditsView']}><Layout><AuditHistoryPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/audits/execute/:auditId',
      element: <AuthGuard permissions={['AuditsExecute']}><Layout><AuditExecutionPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/audits/review/:auditId',
      element: <AuthGuard permissions={['AuditsView']}><Layout><AuditReviewPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/audits/action-items',
      element: <AuthGuard permissions={['ActionItemsView']}><Layout><ActionItemsPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/audits/action-items/:id',
      element: <AuthGuard permissions={['ActionItemsView']}><Layout><ActionItemDetailPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/training/categories',
      element: <AuthGuard permissions={['TrainingAdmin', 'TrainingContentManager', 'TrainingContentViewer']}><Layout><TrainingCategoriesPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/training/modules',
      element: <AuthGuard permissions={['TrainingAdmin', 'TrainingContentManager', 'TrainingContentViewer']}><Layout><TrainingModulesPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/training/modules/builder/:moduleId?',
      element: <AuthGuard permissions={['TrainingAdmin', 'TrainingContentManager']}><Layout><TrainingModuleBuilderPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/training/courses-packs',
      element: <AuthGuard permissions={['TrainingAdmin', 'TrainingContentManager', 'TrainingContentViewer']}><Layout><TrainingCoursesPacksPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/training/courses-packs/builder/:bundleId?',
      element: <AuthGuard permissions={['TrainingAdmin', 'TrainingContentManager']}><Layout><TrainingBundleBuilderPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/training/modules/:moduleId/take',
      element: <AuthGuard permissions={['TrainingAccess']}><Layout><TakeTrainingPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/training/my-training',
      element: <AuthGuard permissions={['TrainingAccess']}><Layout><MyTrainingPage /></Layout></AuthGuard>,
    },
    { //fallback for mismatched invite emails
      path: '/training/my-training',
      element: 
        <Navigate to="/compliance/training/my-training" 
          replace />,
    },
    {
      path: '/compliance/training/assignments',
      element: <AuthGuard permissions={['TrainingAdmin', 'TrainingAssignmentViewer', 'TrainingAssignmentManager']}><Layout><TrainingAssignmentsPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/training/certificates',
      element: <AuthGuard permissions={['TrainingAccess']}><Layout><MyTrainingPage /></Layout></AuthGuard>,
    },
    {
      path: '/compliance/reports',
      element: <AuthGuard permissions={['ViewComplianceReports', 'TrainingSummaryReportRead', 'TrainingCompletionReportRead']}><Layout><ComplianceReportsPage /></Layout></AuthGuard>,
    },
    {
      path: '*',
      element: <AuthGuard permissions={['User']}><Layout><ErrorPage variant='404' /></Layout></AuthGuard>,
    },
  ];

  return <RouterProvider router={createBrowserRouter(routes)} />;
};

const App = () => {
  const apiKey = getApiConfig().googleApiKey;

  useLanguageSwitcher(I18n, () => {
    // Language change callback
  });

  const PUBLISHABLE_KEY = getApiConfig().clerkApiKey;

  if (!PUBLISHABLE_KEY) {
    throw new Error('Missing Publishable Key');
  }

  return (
    <div className="App">
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        afterSignOutUrl='/'
      >
        <UserContextProvider>
          <ConfigurationContextProvider>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="en-gb"
            >
              <AppContent apiKey={apiKey} />
            </LocalizationProvider>
          </ConfigurationContextProvider>
        </UserContextProvider>
      </ClerkProvider>
    </div>
  );
};

export default App;
